import type { CompleteDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { FilterValueType } from "@/components/StructuredSearch/config/structured-search-schema";
import type {
  Filter,
  FilterValue,
  PromptFilterValue,
  StructuredFilterRequest,
  StructuredFilterGroupRequest,
} from "@/components/StructuredSearch/config/types";

const normalizePromptOperator = (operator: string): string => {
  if (operator === "is") {
    return "eq";
  }
  if (operator === "is_not") {
    return "neq";
  }
  return operator;
};

const isPromptFilterValue = (value: unknown): value is PromptFilterValue =>
  typeof value === "object" &&
  value !== null &&
  "prompt_id" in value &&
  typeof value.prompt_id === "number" &&
  (!("prompt_version_number" in value) ||
    value.prompt_version_number === undefined ||
    typeof value.prompt_version_number === "number");

const parsePromptValue = (value: string | object): PromptFilterValue | null => {
  if (typeof value === "object" && isPromptFilterValue(value)) {
    return value;
  }
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value);
      if (isPromptFilterValue(parsed)) {
        return parsed;
      }
    } catch {
      return null;
    }
  }
  return null;
};

const parsePromptIdentifiers = (value: FilterValue): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(item => {
      const promptId = Number(item);
      if (Number.isFinite(promptId)) {
        return String(promptId);
      }
      if (typeof item === "string" && item.trim()) {
        return item.trim();
      }
      return null;
    })
    .filter((promptIdentifier): promptIdentifier is string => promptIdentifier !== null);
};

const isPromptFilterValueList = (value: FilterValue): value is PromptFilterValue[] =>
  Array.isArray(value) && value.every(item => isPromptFilterValue(item));

const buildPromptInFilterGroup = (
  promptValues: PromptFilterValue[],
): StructuredFilterGroupRequest | undefined => {
  if (promptValues.length === 0) {
    return undefined;
  }

  return {
    logic: "OR",
    filters: promptValues.map(promptValue => {
      if (promptValue.prompt_version_number === undefined) {
        return {
          field: "prompt_id",
          operator: "eq",
          value: promptValue.prompt_id,
        };
      }

      return {
        logic: "AND",
        filters: [
          {
            field: "prompt_id",
            operator: "eq",
            value: promptValue.prompt_id,
          },
          {
            field: "prompt_version_number",
            operator: "eq",
            value: promptValue.prompt_version_number,
          },
        ],
      };
    }),
  };
};

export const mapFilterToStructuredRequests = (filter: Filter): StructuredFilterRequest[] => {
  if (filter.field === "signal") {
    const mapped: StructuredFilterRequest = {
      field: "metadata",
      operator: filter.operator,
      value: filter.value,
    };
    if (filter.nestedKey) {
      mapped.nestedKey = filter.nestedKey;
    }
    return [mapped];
  }

  if (filter.type === FilterValueType.Prompt) {
    if (filter.operator === "in" || filter.operator === "not_in") {
      const promptIdentifiers = parsePromptIdentifiers(filter.value);
      if (promptIdentifiers.length === 0) {
        return [];
      }

      return [
        {
          field: "prompt_id",
          operator: filter.operator,
          value: promptIdentifiers,
        },
      ];
    }

    const promptValue = parsePromptValue(filter.value as string | object);
    if (promptValue) {
      const promptFilters: StructuredFilterRequest[] = [
        {
          field: "prompt_id",
          operator: normalizePromptOperator(filter.operator),
          value: promptValue.prompt_id,
        },
      ];

      if (promptValue.prompt_version_number !== undefined) {
        promptFilters.push({
          field: "prompt_version_number",
          operator: "eq",
          value: promptValue.prompt_version_number,
        });
      }

      return promptFilters;
    }
    if (typeof filter.value === "string" && filter.value.trim()) {
      return [
        {
          field: "prompt_id",
          operator: normalizePromptOperator(filter.operator),
          value: filter.value.trim(),
        },
      ];
    }
    return [];
  }

  const mapped: StructuredFilterRequest = {
    field: filter.field,
    operator: filter.operator,
    value: filter.value,
  };
  if (filter.nestedKey) {
    mapped.nestedKey = filter.nestedKey;
  }
  return [mapped];
};

export const mapFiltersToStructuredRequest = (filters: Filter[]): StructuredFilterRequest[] =>
  filters.flatMap(mapFilterToStructuredRequests);

const padDatePart = (value: number) => String(value).padStart(2, "0");

const formatDateWithLocalOffset = (date: Date): string => {
  const offsetMinutes = -date.getTimezoneOffset();
  const offsetSign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteOffsetMinutes = Math.abs(offsetMinutes);
  const offsetHours = Math.floor(absoluteOffsetMinutes / 60);
  const offsetRemainderMinutes = absoluteOffsetMinutes % 60;

  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate(),
  )}T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}:${padDatePart(
    date.getSeconds(),
  )}.${String(date.getMilliseconds()).padStart(3, "0")}${offsetSign}${padDatePart(
    offsetHours,
  )}:${padDatePart(offsetRemainderMinutes)}`;
};

export const buildDateRangeFilter = (
  dateRange: CompleteDateRange,
  options: { preserveLocalOffset?: boolean } = {},
): StructuredFilterRequest => ({
  field: "request_start_time",
  operator: "between",
  value: options.preserveLocalOffset
    ? [formatDateWithLocalOffset(dateRange.from), formatDateWithLocalOffset(dateRange.to)]
    : [dateRange.from.toISOString(), dateRange.to.toISOString()],
});

export const buildStructuredSearchPayload = (
  filters: Filter[],
  dateRange: CompleteDateRange,
  options: { preserveDateRangeLocalOffset?: boolean } = {},
): {
  filterGroup: StructuredFilterGroupRequest;
} => {
  const allFilters: (StructuredFilterRequest | StructuredFilterGroupRequest)[] = [];

  allFilters.push(
    buildDateRangeFilter(dateRange, {
      preserveLocalOffset: options.preserveDateRangeLocalOffset,
    }),
  );

  filters.forEach(filter => {
    if (
      filter.type === FilterValueType.Prompt &&
      (filter.operator === "in" || filter.operator === "not_in") &&
      isPromptFilterValueList(filter.value)
    ) {
      if (filter.operator === "not_in") {
        const promptIds = filter.value
          .map(item => item.prompt_id)
          .filter((id): id is number => typeof id === "number");
        if (promptIds.length > 0) {
          allFilters.push({
            field: "prompt_id",
            operator: "not_in",
            value: promptIds,
          });
        }
      } else {
        const promptGroup = buildPromptInFilterGroup(filter.value);
        if (promptGroup) {
          allFilters.push(promptGroup);
        }
      }
      return;
    }

    allFilters.push(...mapFilterToStructuredRequests(filter));
  });

  return {
    filterGroup: { logic: "AND", filters: allFilters },
  };
};
